import { useEffect } from 'react';
import { useDashboardStore } from '../lib/store';
import styles from './Dashboard.module.css';

export default function Dashboard() {
	const { stats, isLoading, error, fetchStats } = useDashboardStore();

	useEffect(() => {
		fetchStats();
	}, [fetchStats]);

	if (isLoading && !stats) {
		return (
			<div className={styles.loading}>
				<div className={styles.spinner} />
				<span>Loading dashboard...</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className={styles.error}>
				<span>Error loading dashboard: {error}</span>
				<button onClick={fetchStats} className={styles.retryBtn}>
					Retry
				</button>
			</div>
		);
	}

	return (
		<div className={styles.dashboard}>
			<header className={styles.header}>
				<h1 className={styles.title}>Dashboard</h1>
				<p className={styles.subtitle}>Your operator performance at a glance</p>
			</header>

			{/* Today's Stats */}
			<div className={styles.todaySection}>
				<h2 className={styles.sectionLabel}>Today</h2>
				<div className={styles.todayGrid}>
					<div className={styles.todayCard}>
						<span className={styles.todayValue}>{stats?.ggrToday || '$0.00'}</span>
						<span className={styles.todayLabel}>GGR Today</span>
					</div>
					<div className={styles.todayCard}>
						<span className={styles.todayValue}>{stats?.betsToday?.toLocaleString() || 0}</span>
						<span className={styles.todayLabel}>Bets Today</span>
					</div>
				</div>
			</div>

			{/* Stats Cards */}
			<div className={styles.statsGrid}>
				<div className={styles.statCard}>
					<div className={styles.statIcon} data-type="revenue">
						<DollarIcon />
					</div>
					<div className={styles.statContent}>
						<span className={styles.statLabel}>Total GGR</span>
						<span className={styles.statValue}>{stats?.totalGGR || '$0.00'}</span>
					</div>
				</div>

				<div className={styles.statCard}>
					<div className={styles.statIcon} data-type="bets">
						<ChartIcon />
					</div>
					<div className={styles.statContent}>
						<span className={styles.statLabel}>Total Bets</span>
						<span className={styles.statValue}>{stats?.totalBets?.toLocaleString() || 0}</span>
					</div>
				</div>

				<div className={styles.statCard}>
					<div className={styles.statIcon} data-type="players">
						<UsersIcon />
					</div>
					<div className={styles.statContent}>
						<span className={styles.statLabel}>Active Players</span>
						<span className={styles.statValue}>{stats?.activePlayers?.toLocaleString() || 0}</span>
					</div>
				</div>

				<div className={styles.statCard}>
					<div className={styles.statIcon} data-type="sessions">
						<SessionIcon />
					</div>
					<div className={styles.statContent}>
						<span className={styles.statLabel}>Active Sessions</span>
						<span className={styles.statValue}>{stats?.activeSessions || 0}</span>
					</div>
				</div>
			</div>

			{/* Recent Activity */}
			<section className={styles.section}>
				<h2 className={styles.sectionTitle}>Recent Bets</h2>
				<div className={styles.tableWrapper}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Player</th>
								<th>Game</th>
								<th>Amount</th>
								<th>Result</th>
								<th>Time</th>
							</tr>
						</thead>
						<tbody>
							{stats?.recentBets?.length ? (
								stats.recentBets.map((bet) => (
									<tr key={bet.id}>
										<td className={styles.playerId}>{bet.playerId}</td>
										<td>{bet.game}</td>
										<td>{bet.amount}</td>
										<td>
											<span className={`${styles.badge} ${bet.won ? styles.badgeSuccess : styles.badgeDanger}`}>{bet.won ? 'Win' : 'Loss'}</span>
										</td>
										<td className={styles.timestamp}>{formatTime(bet.createdAt)}</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={5} className={styles.emptyState}>
										No recent bets
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</section>
		</div>
	);
}

function formatTime(timestamp: number): string {
	const date = new Date(timestamp);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60000);

	if (diffMins < 1) return 'Just now';
	if (diffMins < 60) return `${diffMins}m ago`;

	const diffHours = Math.floor(diffMins / 60);
	if (diffHours < 24) return `${diffHours}h ago`;

	return date.toLocaleDateString();
}

// Icons
function DollarIcon() {
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
		</svg>
	);
}

function ChartIcon() {
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M3 3v18h18" />
			<path d="M18 17V9" />
			<path d="M13 17V5" />
			<path d="M8 17v-3" />
		</svg>
	);
}

function UsersIcon() {
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="M23 21v-2a4 4 0 0 0-3-3.87" />
			<path d="M16 3.13a4 4 0 0 1 0 7.75" />
		</svg>
	);
}

function SessionIcon() {
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<polyline points="12 6 12 12 16 14" />
		</svg>
	);
}
